import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { AppUser } from '../types/admin';

interface UsersResponse {
  success: boolean;
  data: AppUser[];
}

interface SetAdminResponse {
  success: boolean;
  data: { isPlatformAdmin: boolean };
}

export function useUsers() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<UsersResponse>('/admin/users')
      .then(res => {
        if (!cancelled) {
          setUsers(res.data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load users');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setAdminStatus = useCallback(async (uid: string, isPlatformAdmin: boolean) => {
    // Optimistic update
    setUsers(prev => prev.map(u => (u.uid === uid ? { ...u, isPlatformAdmin } : u)));
    try {
      await api.patch<SetAdminResponse>(`/admin/users/${uid}/admin`, { isPlatformAdmin });
    } catch (err) {
      // Roll back on failure
      setUsers(prev => prev.map(u => (u.uid === uid ? { ...u, isPlatformAdmin: !isPlatformAdmin } : u)));
      throw err;
    }
  }, []);

  return { users, loading, error, setAdminStatus };
}
